import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import Modal from 'react-modal';
import { useNavigate, useParams } from 'react-router-dom';
import { useStores } from '../../hooks/use-stores';
import Setting from '../settings/Setting';
import {
    Block,
    FlexDiv,
    SpaceBetweenDiv,
    TextDefault,
    Heading2,
    Image,
    ThemedButton,
    Heading1,
} from '@awarego-ui/components';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { extractErrorMessage } from '../../utils/helpers';
import PartnerDistributorBackNav from './PartnerDistributorBackNav';

function PartnerBrandingSettings() {
    const { partnerId } = useParams();
    const { commonStore, partnerStore } = useStores();
    const navigate = useNavigate();
    const [partnerName, setPartnerName] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);

    const partner = partnerStore.currentPartner;

    useEffect(() => {
        if (partner) setPartnerName(partner.name || '');
    }, [partner]);

    useEffect(() => {
        void partnerStore.loadSettings(partnerId);
    }, [partnerId, partnerStore]);

    const onCustomizeAppearance = () => {
        navigate('appearance');
    };

    const openEditModal = () => {
        setPartnerName(partner?.name || '');
        setShowEditModal(true);
    };

    const saveName = async () => {
        try {
            await partnerStore.updatePartnerName(partnerId, partnerName);
            commonStore.success('Partner name updated!');
            setShowEditModal(false);
        } catch (e) {
            commonStore.error(
                extractErrorMessage(e) || 'Failed to update partner name'
            );
        }
    };

    const logoUrl = partnerStore.logoUrl;

    if (!partnerStore.settingsLoaded) {
        return <ThemedSpinner />;
    }

    return (
        <>
            <PartnerDistributorBackNav
                partnerId={partnerId}
                sectionLabel="Settings"
            />
        <Block>
            <SpaceBetweenDiv>
                <FlexDiv column>
                    <Heading2 mb={4}>{partner?.name || 'Partner'}</Heading2>
                    <TextDefault lighter>Manage partner settings</TextDefault>
                </FlexDiv>
                {logoUrl ? (
                    <Image
                        link
                        src={logoUrl}
                        height={64}
                        alt="Partner logo"
                        onClick={onCustomizeAppearance}
                    />
                ) : (
                    <Image
                        link
                        src="/static/img/logo-image.svg"
                        alt="Add partner logo"
                        height={64}
                        onClick={onCustomizeAppearance}
                    />
                )}
            </SpaceBetweenDiv>

            <Setting
                name="Name"
                value={partner?.name}
                actionName="Change"
                action={openEditModal}
            />
            <Setting
                name="Appearance"
                actionName="Customize"
                action={onCustomizeAppearance}
                isLast
            />

            <Modal
                isOpen={showEditModal}
                onRequestClose={() => setShowEditModal(false)}
                className={'Modal auto-size-modal'}
                ariaHideApp={false}
            >
                <div className="modalHeader">
                    <Heading1>Rename partner</Heading1>
                    <img
                        className="modal-close-right"
                        src="/static/img/close.svg"
                        alt="Close"
                        onClick={() => setShowEditModal(false)}
                    />
                </div>
                <div className="modalContent">
                    <p>Partner name</p>
                    <input
                        type="text"
                        className="bigInput valid"
                        value={partnerName}
                        onChange={(e) => setPartnerName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && partnerName.trim())
                                saveName();
                        }}
                        autoFocus
                    />
                </div>
                <div className="modalFooter">
                    {partnerStore.savingPartnerName ? (
                        <ThemedSpinner size={50} />
                    ) : (
                        <ThemedButton
                            mode="secondary"
                            text="Done"
                            onClick={saveName}
                            disabled={!partnerName.trim()}
                        />
                    )}
                </div>
            </Modal>
        </Block>
        </>
    );
}

export default observer(PartnerBrandingSettings);
